import classNames from 'classnames';
import { Button } from '@heroui/button';
import {
  IoMdArrowBack,
  IoMdArrowDown,
  IoMdArrowForward,
  IoMdArrowUp,
  IoMdCheckmark,
  IoMdMore,
} from 'react-icons/io';
import { Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger } from '@heroui/dropdown';
import { useTranslation } from 'react-i18next';
import { TbTrashFilled } from 'react-icons/tb';
import { LuPlus } from 'react-icons/lu';
import { Key, useCallback } from 'react';

import MediaImage from '@/components/Media/MediaImage.tsx';
import MediaAudio from '@/components/Media/MediaAudio.tsx';
import MediaVideo from '@/components/Media/MediaVideo.tsx';
import Spinner from '@/components/Spinner/Spinner.tsx';
import { Song, SongMedia, SongMediaColumnWidth } from '@/core/db.ts';
import { useOrderSongMedia, useRemoveSongMedia, useUpdateSongMediaWidth } from '@/hooks/useSongbook.ts';

interface SongMediaItemProps {
  song: Song;
  mediaFile: SongMedia;
  onAddFile: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

function SongMediaItem({ song, mediaFile, onAddFile, isLast, isFirst }: SongMediaItemProps) {
  const { t } = useTranslation();
  const { isPending: isRemovePending, mutate: removeSongMedia } = useRemoveSongMedia(song);
  const { isPending: isOrderPending, mutate: reorderSong } = useOrderSongMedia(song);
  const { mutate: updateWidth } = useUpdateSongMediaWidth(mediaFile.id);

  const width = mediaFile.width || 'auto';
  const widths = ['auto', '2/3', '1/2', '1/3', '1/4'];

  const isPending = isRemovePending || isOrderPending;

  const onAction = useCallback((key: Key) => {
    if (key.toString().startsWith('column_')) {
      let newWidth: SongMediaColumnWidth = 'auto';

      switch (key.toString().replace('column_', '')) {
        case '2/3':
          newWidth = '2/3';
          break;
        case '1/2':
          newWidth = '1/2';
          break;
        case '1/3':
          newWidth = '1/3';
          break;
        case '1/4':
          newWidth = '1/4';
          break;
      }

      updateWidth(newWidth);

      return;
    }

    switch (key) {
      case 'delete':
        removeSongMedia(mediaFile.id);
        break;
      case 'add':
        onAddFile();
        break;
      case 'orderUp':
        reorderSong({ id: mediaFile.id, direction: 'up' });
        break;
      case 'orderDown':
        reorderSong({ id: mediaFile.id, direction: 'down' });
        break;
    }
  }, [mediaFile, removeSongMedia, onAddFile, reorderSong, updateWidth]);

  return (
    <div
      className={classNames(
        'relative group rounded transition-colors outline-2 outline-transparent p-2 -m-2',
        {
          'hover:outline-secondary': !isPending,
        }
      )}
    >
      {isPending && (
        <div className="absolute top-0 left-0 w-full h-full flex justify-center items-center z-10 bg-default-50/50">
          <Spinner />
        </div>
      )}
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          {!isPending ? (
            <Button
              isIconOnly
              className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 rounded-bl-sm"
              color="secondary"
              radius="none"
              size="md"
            >
              <IoMdMore />
            </Button>
          ) : <div />}
        </DropdownTrigger>
        <DropdownMenu onAction={onAction}>
          <DropdownSection showDivider={song.orientation === 'horizontal'} title={t('songbook.song.actions')}>
            {!isFirst ? (
              <DropdownItem
                key="orderDown"
                className="text-foreground"
                color="default"
                startContent={song.orientation === 'horizontal' ? <IoMdArrowBack /> : <IoMdArrowUp />}
              >
                {song.orientation === 'horizontal' ? t('songbook.song.moveLeft') : t('songbook.song.moveUp')}
              </DropdownItem>
            ) : null}
            {!isLast ? (
              <DropdownItem
                key="orderUp"
                className="text-foreground"
                color="default"
                startContent={song.orientation === 'horizontal' ? <IoMdArrowForward /> : <IoMdArrowDown />}
              >
                {song.orientation === 'horizontal' ? t('songbook.song.moveRight') : t('songbook.song.moveDown')}
              </DropdownItem>
            ) : null}
            <DropdownItem
              key="add"
              className="text-foreground"
              color="secondary"
              startContent={<LuPlus />}
            >
              {t('songbook.song.mediaAdd')}
            </DropdownItem>
            <DropdownItem
              key="delete"
              className="text-danger"
              color="danger"
              startContent={<TbTrashFilled />}
            >
              {t('songbook.song.removeMedia')}
            </DropdownItem>
          </DropdownSection>
          {song.orientation === 'horizontal' ? (
            <DropdownSection title={t('songbook.song.columnWidth')}>
              {widths.map((w) => (
                <DropdownItem
                  key={['column', w].join('_')}
                  className="text-foreground capitalize"
                  startContent={width === w ? <IoMdCheckmark /> : <div className="w-[14px]" />}
                >
                  {w}
                </DropdownItem>
              ))}
            </DropdownSection>
          ) : null}
        </DropdownMenu>
      </Dropdown>
      <div className="flex justify-center">
        {mediaFile.type === 'image' && <MediaImage mediaFile={mediaFile} />}
        {mediaFile.type === 'audio' && <MediaAudio mediaFile={mediaFile} />}
        {mediaFile.type === 'video' && <MediaVideo mediaFile={mediaFile} />}
      </div>
    </div>
  );
}

export default SongMediaItem;
